import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addScopeZip,
  deleteScopeZip,
  disableScraper,
  enableScraper,
  fetchScopeZips,
  statusBadgeClass,
  type ZipCodeRecord
} from '$lib/api/adminScope';

// Operator-facing scope config page. Matches the existing admin pages'
// austere Tailwind palette (no design polish — this is an operator
// surface, not customer-facing).
//
// Soft-refresh every 30s while any zip is in 'enumerating' or
// 'draining' so the operator sees provisioning progress without
// having to hit refresh.

const POLL_INTERVAL_MS = 30_000;

export function AdminScopePage() {
  const qc = useQueryClient();
  const [newZip, setNewZip] = useState('');
  const [newNote, setNewNote] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const zipsQuery = useQuery({
    queryKey: ['admin', 'scope-zips'],
    queryFn: () => fetchScopeZips(true),
    // Adaptive polling: cheap enough to poll every 30s when any zip is
    // mid-provision; otherwise let the user pull to refresh.
    refetchInterval: (q) => {
      const items = q.state.data?.items ?? [];
      const inProgress = items.some(
        (i) => i.status === 'enumerating' || i.status === 'draining'
      );
      return inProgress ? POLL_INTERVAL_MS : false;
    }
  });

  const addMutation = useMutation({
    mutationFn: (body: { zip_code: string; note?: string | null }) =>
      addScopeZip(body),
    onSuccess: () => {
      setNewZip('');
      setNewNote('');
      setErrorMsg(null);
      qc.invalidateQueries({ queryKey: ['admin', 'scope-zips'] });
    },
    onError: (err: Error) => {
      setErrorMsg(err.message || 'Failed to add zip');
    }
  });

  const enableMutation = useMutation({
    mutationFn: (zip: string) => enableScraper(zip),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'scope-zips'] }),
    onError: (err: Error) => setErrorMsg(err.message)
  });

  const disableMutation = useMutation({
    mutationFn: (zip: string) => disableScraper(zip),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'scope-zips'] }),
    onError: (err: Error) => setErrorMsg(err.message)
  });

  const deleteMutation = useMutation({
    mutationFn: (zip: string) => deleteScopeZip(zip),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'scope-zips'] }),
    onError: (err: Error) => setErrorMsg(err.message)
  });

  const items = useMemo(() => zipsQuery.data?.items ?? [], [zipsQuery.data]);

  const submitAdd = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    if (!/^\d{4}$/.test(newZip)) {
      setErrorMsg('Zip must be a 4-digit Danish postal code.');
      return;
    }
    addMutation.mutate({ zip_code: newZip, note: newNote.trim() || null });
  };

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col overflow-y-auto">
      <header className="flex shrink-0 items-center gap-3 border-b border-rule bg-surface-2 px-4 py-3">
        <h1 className="text-md font-semibold text-fg">Scope configuration</h1>
        <span className="text-xs text-fg-muted">
          {items.length} zip{items.length === 1 ? '' : 's'}
        </span>
      </header>

      <section className="border-b border-rule bg-surface-2 px-4 py-3">
        <form onSubmit={submitAdd} className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs text-fg-muted">Zip code</span>
            <input
              type="text"
              inputMode="numeric"
              maxLength={4}
              pattern="\d{4}"
              value={newZip}
              onChange={(e) => setNewZip(e.target.value.replace(/\D/g, ''))}
              className="h-8 w-24 rounded border border-rule bg-surface px-2 text-sm font-mono text-fg"
              placeholder="8000"
              required
              data-testid="admin-scope-zip-input"
            />
          </label>
          <label className="flex flex-1 flex-col gap-1 min-w-[12rem]">
            <span className="text-xs text-fg-muted">Note (optional)</span>
            <input
              type="text"
              maxLength={500}
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              className="h-8 rounded border border-rule bg-surface px-2 text-sm text-fg"
              placeholder="Why are we adding this zip?"
              data-testid="admin-scope-note-input"
            />
          </label>
          <button
            type="submit"
            disabled={addMutation.isPending || newZip.length !== 4}
            className="h-8 rounded bg-fg px-3 text-xs font-medium text-surface disabled:opacity-50"
            data-testid="admin-scope-add-button"
          >
            {addMutation.isPending ? 'Adding…' : 'Add zip'}
          </button>
          {errorMsg && (
            <span className="text-xs text-red-600 dark:text-red-400" role="alert">
              {errorMsg}
            </span>
          )}
        </form>
      </section>

      <section className="flex-1 overflow-x-auto p-4">
        {zipsQuery.isPending && (
          <div className="text-xs text-fg-muted">Loading scope zips…</div>
        )}
        {zipsQuery.isError && (
          <div className="text-xs text-red-600 dark:text-red-400" role="alert">
            Failed to load scope zips.
          </div>
        )}
        {zipsQuery.data && (
          <table className="w-full border-collapse text-sm" data-testid="admin-scope-table">
            <thead>
              <tr className="border-b border-rule text-left text-xs uppercase tracking-wide text-fg-muted">
                <th className="px-2 py-2">Zip</th>
                <th className="px-2 py-2">Status</th>
                <th className="px-2 py-2">BBR progress</th>
                <th className="px-2 py-2">Scraper</th>
                <th className="px-2 py-2">Ready at</th>
                <th className="px-2 py-2">Note</th>
                <th className="px-2 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <ZipRow
                  key={row.zip_code}
                  row={row}
                  onEnable={() => enableMutation.mutate(row.zip_code)}
                  onDisable={() => disableMutation.mutate(row.zip_code)}
                  onDelete={() => {
                    if (window.confirm(`Soft-delete zip ${row.zip_code}?`)) {
                      deleteMutation.mutate(row.zip_code);
                    }
                  }}
                />
              ))}
              {items.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-2 py-6 text-center text-xs text-fg-muted">
                    No scope zips configured. Add one above.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

interface ZipRowProps {
  row: ZipCodeRecord;
  onEnable: () => void;
  onDisable: () => void;
  onDelete: () => void;
}

function ZipRow({ row, onEnable, onDisable, onDelete }: ZipRowProps) {
  const progress =
    row.bbr_total && row.bbr_total > 0
      ? Math.round((100 * (row.bbr_done ?? 0)) / row.bbr_total)
      : null;

  return (
    <tr
      className="border-b border-rule"
      data-testid={`admin-scope-row-${row.zip_code}`}
    >
      <td className="px-2 py-2 font-mono text-fg">{row.zip_code}</td>
      <td className="px-2 py-2">
        <span
          className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${statusBadgeClass(
            row.status,
            row.scraper_enabled
          )}`}
        >
          {row.status}
        </span>
      </td>
      <td className="px-2 py-2 text-xs text-fg-muted">
        {row.bbr_total != null ? (
          <>
            <span className="num text-fg">{row.bbr_done ?? 0}</span>
            {' / '}
            <span className="num">{row.bbr_total}</span>
            {progress != null && (
              <span className="ml-2 text-fg-muted">({progress}%)</span>
            )}
          </>
        ) : (
          '—'
        )}
      </td>
      <td className="px-2 py-2">
        {row.scraper_enabled ? (
          <span className="text-xs text-green-700 dark:text-green-400">enabled</span>
        ) : (
          <span className="text-xs text-fg-muted">disabled</span>
        )}
      </td>
      <td className="px-2 py-2 text-xs text-fg-muted">
        {row.ready_at ? new Date(row.ready_at).toLocaleString() : '—'}
      </td>
      <td className="px-2 py-2 text-xs text-fg-muted truncate max-w-[16rem]">
        {row.note ?? ''}
      </td>
      <td className="px-2 py-2 text-right">
        <div className="inline-flex gap-2">
          {row.status === 'ready' && !row.scraper_enabled && (
            <button
              type="button"
              onClick={onEnable}
              className="rounded border border-rule px-2 py-1 text-xs text-fg hover:bg-surface-2"
              data-testid={`admin-scope-enable-${row.zip_code}`}
            >
              Enable
            </button>
          )}
          {row.scraper_enabled && (
            <button
              type="button"
              onClick={onDisable}
              className="rounded border border-rule px-2 py-1 text-xs text-fg hover:bg-surface-2"
              data-testid={`admin-scope-disable-${row.zip_code}`}
            >
              Disable
            </button>
          )}
          {row.status !== 'disabled' && (
            <button
              type="button"
              onClick={onDelete}
              className="rounded border border-rule px-2 py-1 text-xs text-red-600 hover:bg-surface-2 dark:text-red-400"
              data-testid={`admin-scope-delete-${row.zip_code}`}
            >
              Delete
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}
